import { css } from "styled-components";
import { TColor, TTypographyVariant } from "../types";
import { emToPx, hexToRGBA } from "./theme";

export function colorMixin(color: TColor, alpha?: number): ReturnType<typeof css>;
export function colorMixin(color: string, alpha?: number): ReturnType<typeof css>;
export function colorMixin(color: string, alpha?: number) {
    return css`
        ${({ theme }) => {
            const hex = theme.v2.colors[color as TColor] || color;

            return typeof alpha === "number" ? hexToRGBA(hex, alpha) : hex;
        }}
    `;
}

const BORDER_SIDES = ["top", "bottom", "left", "right"] as const;

interface IBorderParams {
    color: TColor;
    radius?: boolean;
    sides?: Partial<Record<(typeof BORDER_SIDES)[number], boolean>>;
}

export const borderMixin = ({
    color = "border-primary-100",
    radius,
    sides = {
        top: true,
        bottom: true,
        left: true,
        right: true
    }
}: IBorderParams) => css`
    ${BORDER_SIDES.map(
        side =>
            sides[side] &&
            css`
                border-${side}: 1px solid ${colorMixin(color)};
            `
    )};
    ${radius &&
    css`
        border-radius: 4px;
    `};
`;

export const typographyMixin = (size: TTypographyVariant) => css`
    ${({ theme }) => css`
        font-weight: ${theme.v2.typography[size].fontWeight};
        font-size: ${emToPx(theme.v2.typography[size].fontSize)}px;
        letter-spacing: ${emToPx(theme.v2.typography[size].letterSpacing)}px;
        text-decoration: ${theme.v2.typography[size].textDecoration};
        line-height: ${emToPx(theme.v2.typography[size].lineHeight)}px;
    `}
`;

export function spacingMixin(...spacings: Array<number | undefined>): string {
    if (!spacings.length) {
        return spacingMixin(1);
    }

    return spacings.map((spacing = 1) => `${spacing * 8}px`).join(" ");
}

export const focusShadowMixin = (mainColor: TColor, shadowColor: TColor) => css`
    box-shadow: 0px 0px 0px 4px ${colorMixin(mainColor)}, 0px 0px 1px 0px ${colorMixin(shadowColor, 0.16)},
        0px 1px 2px 0px ${colorMixin(shadowColor, 0.24)};
    outline: none;
`;
